import { Editor } from "codemirror";
import * as React from "react";

import { CheckCircle, Warning } from "@livechat/design-system-icons";
import { DesignToken, Icon, SpacingToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { withVar } from "themes/helpers";

import { ContentType, TEMPLATE_MAX_LENGTH } from "../constants";
import { isHTMLValid, isPlainTextValid } from "../helpers";

import { ParameterSelect } from "./ParameterSelect";
import { StyledBar } from "./styled";

const getPlainTextValidation = (plainText: string): string => {
  if (!plainText || !plainText.includes("{{{message.text}}}")) {
    return "Add “Message” parameter";
  }

  if (isPlainTextValid(plainText)) {
    return "OK";
  }

  if (plainText.length > TEMPLATE_MAX_LENGTH) {
    return "Template is too long (more than 32,768 characters)";
  }

  return "Add “Message” parameter";
};

const getHTMLValidation = (html: string): string => {
  if (!html || !html.includes("{{{message.html}}}")) {
    return "Add “Message” parameter";
  }

  if (html.match(/<body[\s\S]*>/gim) === null) {
    return "Add opening <body> tag";
  }

  if (html.match(/<\/\s*body\s*>/gim) === null) {
    return "Add closing </body> tag";
  }

  if (html.length > TEMPLATE_MAX_LENGTH) {
    return "Template is too long (more than 32,768 characters)";
  }

  return "OK";
};

interface IValidationBarProps {
  contentType: ContentType;
  templates: Record<ContentType, string>;
  editor: React.RefObject<null | Editor>;
  disabled: boolean;
}

const ValidationBar: React.FC<IValidationBarProps> = ({ contentType, templates, editor, disabled }) => {
  const validationStatus =
    contentType === ContentType.PlainText
      ? isPlainTextValid(templates[ContentType.PlainText])
      : isHTMLValid(templates[ContentType.HTML]);

  const icon = validationStatus ? CheckCircle : Warning;
  const color = withVar(validationStatus ? DesignToken.ActionPositiveDefault : DesignToken.ActionWarningDefault);
  const text =
    contentType === ContentType.PlainText
      ? getPlainTextValidation(templates[ContentType.PlainText])
      : getHTMLValidation(templates[ContentType.HTML]);

  return (
    <StyledBar>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
          <Text className="template-status">Template status:</Text>
          <Icon source={icon} size="small" customColor={color} />
          <Text>{text}</Text>
        </Flex>
        <Flex alignItems="center">
          <ParameterSelect disabled={disabled} contentType={contentType} editor={editor} />
        </Flex>
      </Flex>
    </StyledBar>
  );
};

export default ValidationBar;
